import { createContext, useState, useEffect, useContext, useRef } from 'react';

const ImageContext = createContext();

const STORAGE_KEY = 'agape_photos_cache';
const CACHE_VERSION = 'v3'; // Increment when photo data structure changes
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * ImageProvider - Loads and caches photos from the backend on app start.
 * Photos are cached in sessionStorage to avoid re-fetching on page refresh.
 * Cache expires after 24 hours or when version changes.
 * 
 * @param {Object} props
 * @param {Function} props.fetchPhotos - Function that returns a promise with photo data
 * @param {ReactNode} props.children
 */
export function ImageProvider({ children, fetchPhotos }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use a ref to track if images have already been loaded
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once on mount
    if (hasLoadedRef.current) return;
    
    async function loadImages() {
      try {
        hasLoadedRef.current = true;
        setLoading(true);

        // Try to load from sessionStorage first
        const cached = sessionStorage.getItem(STORAGE_KEY);
        if (cached) {
          try {
            const { version, timestamp, data } = JSON.parse(cached);
            const age = Date.now() - timestamp;
            
            // Use cache if version matches and not expired
            if (version === CACHE_VERSION && age < CACHE_DURATION) {
              console.log('[ImageContext] Loaded from cache:', data?.length || 0, 'images');
              setImages(data || []);
              setLoading(false);
              return;
            } else {
              console.log('[ImageContext] Cache expired or version mismatch, fetching fresh data');
              sessionStorage.removeItem(STORAGE_KEY);
            }
          } catch (parseErr) {
            console.warn('[ImageContext] Cache parse error, will fetch fresh:', parseErr);
            sessionStorage.removeItem(STORAGE_KEY);
          }
        }

        // Fetch fresh data from backend
        console.log('[ImageContext] Fetching photos from backend...');
        const allImages = await fetchPhotos();
        setImages(allImages || []);
        
        // Cache the results
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            version: CACHE_VERSION,
            timestamp: Date.now(),
            data: allImages || []
          }));
          console.log('[ImageContext] Cached', allImages?.length || 0, 'images');
        } catch (storageErr) {
          console.warn('[ImageContext] Failed to cache images:', storageErr);
          // Continue anyway - caching is optional
        }
        
        console.log('[ImageContext] Loaded images:', allImages?.length || 0);
      } catch (err) {
        console.error('[ImageContext] Error fetching images:', err);
        setError(err.message);
        setImages([]);
        hasLoadedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    }

    loadImages();
  }, []); // Empty deps - only run once on mount

  return (
    <ImageContext.Provider value={{ images, loading, error }}>
      {children}
    </ImageContext.Provider>
  );
}

export function useImages() {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useImages must be used within an ImageProvider');
  }
  return context;
}
